import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, Mail, Printer, Send } from 'lucide-react'
import { dropdownItem, dropdownMenu } from '@/lib/form-styles'
import { downloadFile, extractFilename } from '@/lib/download'
import { exportLaden } from '@/lib/export-request'
import { useTerminology } from '@/lib/useTerminology'
import { MailExportDialog } from '@/components/mail/MailExportDialog'
import { workOrderExportRequest } from '../api/work-order.api'
import { AssignDialog } from './cockpit/AssignDialog'

/** Fehlertext aus einer Arraybuffer-Antwort — wie im `ExportButton`. */
function fehlerAus(err: unknown, fallback: string): string {
  const antwort = (err as { response?: { status?: number; data?: unknown } })?.response
  if (!antwort) return fallback
  const roh = antwort.data
  if (roh instanceof ArrayBuffer) {
    try {
      const geparst = JSON.parse(new TextDecoder().decode(roh)) as { message?: string; code?: string }
      if (geparst?.code === 'WORK_ORDER_NOT_RELEASED') return 'Den Auftrag gibt es erst nach der Freigabe.'
      return `${antwort.status ?? ''}: ${geparst?.message ?? fallback}`
    } catch {
      return fallback
    }
  }
  return fallback
}

/**
 * AP-35 Nachtrag 9 — **„Auftrag“** (ersetzt „Arbeitskarte“).
 *
 * Drei Wege, alle mit derselben Folge: Der freigegebene Schritt wird
 * „zugeteilt“ (Auftraggeber 19.9.2026).
 * - **Drucken** — das PDF, eine Seite je Schritt;
 * - **per E-Mail** — dieselbe Datei über den `MailExportDialog`;
 * - **An PWA senden** (nur am Schritt) — die Einteilung aus dem Cockpit: Der
 *   Mitarbeiter findet den Auftrag in der PWA unter „Aufträge“.
 *
 * Nicht der allgemeine `ExportButton`: nur PDF, ohne Ausrichtung, dafür mit dem
 * dritten Weg. Sichtbar nur mit `projects.export.run`; das prüft der Aufrufer,
 * ebenso `darfSenden` (`projects.assign.manage`).
 */
export function WorkOrderButton({
  projectId,
  projectNumber,
  positionId = null,
  positionNumber,
  step,
  darfSenden = false,
  kompakt = false,
  label,
  onDone,
}: {
  projectId: string
  projectNumber: string
  positionId?: string | null
  positionNumber?: number
  /** Der Auftrag eines Schritts; ohne: alle freigegebenen Schritte (der Position). */
  step?: { id: string; number: number }
  darfSenden?: boolean
  /** Kleiner Knopf für den Block rechts am Schritt. */
  kompakt?: boolean
  label?: string
  /** Nach Druck, Mail oder Einteilung — der Status hat sich geändert. */
  onDone?: () => void
}) {
  const { t } = useTranslation()
  const terms = useTerminology()
  const [offen, setOffen] = useState(false)
  const [mailOffen, setMailOffen] = useState(false)
  const [sendenOffen, setSendenOffen] = useState(false)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const anfrage = () => workOrderExportRequest({ projectId, positionId, stepId: step?.id })
  const titel = t('mailExport.titles.EXPORT_WORK_ORDER')
  const betreff = [
    titel,
    `${terms.project} ${projectNumber}`,
    positionNumber != null ? `${terms.position} ${positionNumber}` : null,
    step ? `Nr. ${step.number}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  async function herunterladen() {
    setOffen(false)
    setLaeuft(true)
    setFehler(null)
    try {
      const res = await exportLaden(anfrage())
      const name = extractFilename(res.headers['content-disposition'] as string | undefined, `Auftrag_${projectNumber}.pdf`)
      downloadFile(res.data, name, 'application/pdf')
      onDone?.()
    } catch (e) {
      setFehler(fehlerAus(e, 'Der Auftrag konnte nicht erzeugt werden.'))
    } finally {
      setLaeuft(false)
    }
  }

  const eintrag = `${dropdownItem} flex items-center gap-2`
  const farbe = { color: 'hsl(var(--card-foreground))' }

  return (
    <div className="relative inline-block">
      <button
        type="button"
        disabled={laeuft}
        className={
          kompakt
            ? 'inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-50'
            : 'inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-50'
        }
        onClick={() => {
          setOffen((o) => !o)
          setFehler(null)
        }}
        aria-haspopup="menu"
        aria-expanded={offen}
      >
        <Printer className={kompakt ? 'h-3 w-3' : 'h-4 w-4'} /> {laeuft ? 'Erzeuge …' : (label ?? (step ? 'Auftrag' : 'Aufträge'))}
      </button>

      {offen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOffen(false)} />
          <div role="menu" className={`${dropdownMenu} right-0 mt-1 min-w-[200px]`} style={{ background: 'hsl(var(--card))', ...farbe }}>
            <button type="button" className={eintrag} style={farbe} onClick={herunterladen}>
              <Download className="h-4 w-4" /> Drucken (PDF)
            </button>
            <button
              type="button"
              className={`${eintrag} border-t border-border`}
              style={farbe}
              onClick={() => {
                setOffen(false)
                setMailOffen(true)
              }}
            >
              <Mail className="h-4 w-4" /> {t('mailExport.menu')}
            </button>
            {step && darfSenden && (
              <button
                type="button"
                className={`${eintrag} border-t border-border`}
                style={farbe}
                onClick={() => {
                  setOffen(false)
                  setSendenOffen(true)
                }}
              >
                <Send className="h-4 w-4" /> An PWA senden …
              </button>
            )}
          </div>
        </>
      )}

      {/* Die Dialoge stehen im Block rechts einer Zeile: Enter, Esc und Klicks dürfen
          nicht zum Zeilenblock durchlaufen — der spränge sonst in die nächste Zeile. */}
      <div onKeyDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <MailExportDialog
          open={mailOffen}
          onClose={() => {
            setMailOffen(false)
            onDone?.()
          }}
          source="EXPORT_WORK_ORDER"
          title={titel}
          defaultSubject={betreff}
          request={() => anfrage()}
          formats={['pdf']}
        />

        {sendenOffen && step && (
          <AssignDialog
            stepIds={[step.id]}
            onClose={() => setSendenOffen(false)}
            onDone={() => {
              setSendenOffen(false)
              onDone?.()
            }}
          />
        )}
      </div>

      {fehler && (
        <div className="absolute right-0 top-full z-30 mt-1 max-w-xs rounded-md border border-destructive bg-popover px-3 py-2 text-xs text-destructive shadow-md">
          {fehler}
        </div>
      )}
    </div>
  )
}
